/// <amd-module name="ArtifactPolicy/Templates/TemplateInputs" />
import React = require("react");

import { TextField } from "VSSUI/TextField";
import { FormItem } from "VSSUI/FormItem";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { announce } from "VSS/Core/Util/Accessibility";
import { Observer } from "VSSUI/Observer";
import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";

import * as Resources from "ArtifactPolicy/Templates/Resources";
import { getObservableKey, basicPolicyTemplatecontributionId } from "ArtifactPolicy/Templates/PolicyDefinition";
import { IArtifactPolicyTemplateContributionInput, IArtifactPolicyTemplateContribution } from "ArtifactPolicy/Templates/Types";

export interface ITemplateInputsProps extends IVssComponentProperties {
    selectedContributionIds: string[];
    inputsObservableDictionary: { [key: string]: IObservableValue<string> };
    inputsUpdatedCallback: () => void;
}

export class TemplateInputs extends VssComponent<ITemplateInputsProps, IVssComponentContext> {
    public constructor(props: ITemplateInputsProps, context: IVssComponentContext) {
        super(props, context);

        this._queryThroughInputs((inputs, contribution, cid) => {
            return inputs.forEach(input => {
                const key = getObservableKey(cid, input.id);
                if (!props.inputsObservableDictionary[key]) {
                    props.inputsObservableDictionary[key] = new ObservableValue<string>("");
                    this.props.inputsUpdatedCallback();
                }
            });
        });
    }

    public render(): React.ReactNode {
        let textFieldIndex = 0;
        return (<div className={"flex-column policy-inputs-content"} >
            <Observer {...this.props.inputsObservableDictionary}>
                {(observedProps: { [key: string]: string }) => {
                    return (<React.Fragment>
                        <div>{Resources.FillInputsHeading}</div>
                        {
                            this._queryThroughInputs((inputs, contribution, cid) => {
                                return (<React.Fragment key={"art-pol-template-" + cid}>
                                    <div className={"margin-vertical-20 title-s"}>{contribution.displayName}</div>
                                    {
                                        inputs.length > 0 ?
                                            inputs.map(input => <FormItem
                                                error={!observedProps[getObservableKey(cid, input.id)]}
                                                className={"font-weight-semibold"}
                                                key={"input-" + input.id}
                                                label={input.displayName}
                                                ariaLabel={format(Resources.TemplateInputAriaLabel, contribution.displayName, input.displayName)}>
                                                <TextField
                                                    value={observedProps[getObservableKey(cid, input.id)]}
                                                    placeholder={input.placeholder}
                                                    onChange={(event, val) => this._onInputValueChange(val, getObservableKey(cid, input.id))}
                                                    autoFocus={textFieldIndex++ === 0}
                                                />
                                            </FormItem>)
                                            : this._getNoInputContent(contribution.displayName)
                                    }
                                </React.Fragment>);
                            })}
                    </React.Fragment>);
                }}
            </Observer>
        </div>);
    }

    private _getNoInputContent(displayName: string): JSX.Element {
        let noInputString = Resources.NoInputs;
        announce(format(Resources.TemplateInputAriaLabel, displayName, noInputString));

        return (
            <div className="secondary-text">
                {noInputString}
            </div>
        );
    }

    private _onInputValueChange = (newValue: string, observableKey: string) => {
        this.props.inputsObservableDictionary[observableKey].value = newValue;
        this.props.inputsUpdatedCallback();
    }

    private _queryThroughInputs<T>(action: (inputs: IArtifactPolicyTemplateContributionInput[], contribution: IArtifactPolicyTemplateContribution, contributionId: string) => T): T[] {
        return this.props.selectedContributionIds.filter(id => id !== basicPolicyTemplatecontributionId).map(cid => {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const contribution = contributionService.getContribution<IArtifactPolicyTemplateContribution>(cid)!;
            const inputs: IArtifactPolicyTemplateContributionInput[] = contribution.inputs;
            return action(inputs, contribution, cid);
        });
    }
}
